import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export function CustomSelect({ 
  value, 
  onChange, 
  options, 
  placeholder = 'Select...', 
  className = '',
  'data-testid': testId 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  
  // Find selected option label
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption?.label || placeholder;
  
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSelect = (option) => {
    if (option.disabled) return;
    onChange({ target: { value: option.value } });
    setIsOpen(false);
  };
  
  return (
    <div className={`relative ${className}`} ref={ref} data-testid={testId}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 h-9 px-3 bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 text-sm text-[#30302F] dark:text-white hover:border-[#98139C] transition-colors w-full min-w-[120px]"
      >
        <span className="truncate">{displayValue}</span>
        <ChevronDown size={14} className={`text-[#30302F]/70 dark:text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 shadow-lg py-1 z-50 max-h-60 overflow-y-auto">
          {options.map((option, idx) => (
            <button
              key={option.value || `placeholder-${idx}`}
              type="button"
              onClick={() => handleSelect(option)}
              disabled={option.disabled}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                option.disabled
                  ? 'text-[#30302F]/40 dark:text-white/40 cursor-not-allowed italic'
                  : option.value === value 
                    ? 'bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] font-medium' 
                    : 'text-[#30302F] dark:text-white hover:bg-[#faf8fb] dark:hover:bg-white/5'
              }`}
              data-testid={`${testId}-option-${option.value || idx}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CustomSelect;
